/* eslint-disable no-restricted-imports -- File defines overrides */
import React, { useEffect } from 'react';

import type { NavigateFunction } from '@remix-run/react';
import { Navigate as RemixNavigate, useNavigate as useRemixNavigate } from '@remix-run/react';

import { LoadingPage } from 'platform/components/LoadingPage';
import { SystemError } from 'platform/Error';
import { useStalePageCheck } from 'platform/hooks/useReloadStalePage';
import { useObservability } from 'platform/observability';
import { useToPath } from 'platform/preserveQueryParams';

import { normalizePathname, usePath } from 'shared/hooks/usePath';
import { trackNavigationEvent } from 'shared/hooks/useTrackPerformance';

import type { NavigateOptions } from './types';

/* eslint-enable no-restricted-imports  -- File defines overrides */

export type { To } from './types';

/**
 * Component that tracks all the redirects and ensures that url params are passed to the new route
 *
 * @override
 */
export const Navigate: React.FC<
  NavigateOptions & {
    isFallback?: boolean;
    customDisallowedSearchParams?: string[];
  }
> = ({ to, isFallback, customDisallowedSearchParams, ...props }) => {
  const { trackEvent } = useObservability();
  const from = usePath();
  const checkStalePageAndAuth = useStalePageCheck();

  if (!to) {
    throw new SystemError('Navigate component must have a properly formatted `to` prop');
  }

  checkStalePageAndAuth();

  const getToPath = useToPath(customDisallowedSearchParams);
  const toPath = getToPath(to);
  useEffect(() => {
    trackEvent(props.replace ? 'redirect' : 'navigate', normalizePathname(toPath.pathname ?? ''), {
      from,
      fallback: isFallback,
      ...props?.state,
    });
    trackNavigationEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect once
  }, []);

  return (
    <>
      <RemixNavigate to={toPath} {...props} />
      {/**
       * We need to return a loading indicator here because returning an `<Navigate />` component on it's own cases Remix to render a blank screen (null). Rendering a navigate on TOP of UI resolves the temporary flash of a blank scree
       */}
      <LoadingPage />
    </>
  );
};

/**
 * Component that tracks all the redirects and ensures that url params are passed to the new route
 * @override
 */
export const useNavigate = (customDisallowedSearchParams?: string[]): NavigateFunction => {
  const navigate = useRemixNavigate();
  const { trackEvent } = useObservability();
  const from = usePath();
  const getToPath = useToPath(customDisallowedSearchParams);
  const checkStalePageAndAuth = useStalePageCheck();

  return (...props) => {
    checkStalePageAndAuth();
    trackNavigationEvent();

    // Numerically navigate the history stack
    if (typeof props[0] === 'number') {
      const delta: number = props[0];
      trackEvent('navigate', `Navigate the history stack by ${delta} `, { from });
      navigate(delta);
      return;
    }

    // Navigate to specific new location while maintaining search params
    const to = props[0];
    const options = props[1] as NavigateOptions | undefined;
    const toPath = getToPath(to);
    trackEvent(
      options?.replace ? 'redirect' : 'navigate',
      normalizePathname(toPath.pathname ?? ''),
      { from, ...options?.state },
    );
    navigate(toPath, options);
  };
};

/**
 * Fallback component that tracks all the redirects and ensures that url params are passed to the new route. This is a simple extension of the Navigate component defined above
 * @override
 */
export const Fallback: React.FC<NavigateOptions & { customDisallowedSearchParams?: string[] }> = (
  props,
) => <Navigate {...props} isFallback />;
